import { Alert, Button, Checkbox, Collapse, Form, Modal } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { FeatureFlags } from '../../common/models';
import { UploadedPackagesVisualStore } from '../stores';
import { FeatureFlagsTreeRenderer } from '../../common/components';
import {
    transformFeatureFlagsAsRecord,
    setFormFeatureFlags,
    FeatureFlagsOverride,
    convertAlphaConfigsToFeatureFlags
} from '../../common/misc/FeatureFlagsHelpers';
import { FeatureFlagProcessingPlan, featureFlagProcessingLabels } from '../models/FeatureFlagProcessingPlan';
import { FeatureFlagsPreviewState } from './useFeatureFlagsPreview';
import { FeatureFlagsPreview } from './FeatureFlagsPreview';
import { HasPermission } from '../../authorization/components/HasPermission';
import { AppPermissions } from '../../authorization/Permissions';
import { featureFlagsPreviewKeyOf } from '../models/FeatureFlagsPlanning';

const formId = 'package-feature-flags-form';

type PlanState = FeatureFlagsPreviewState<FeatureFlagProcessingPlan>;

type Props = {
    visible: boolean;
    changeVisible: (visible: boolean) => void;
    currentFeatureFlags: FeatureFlags;
    currentPackageId: string;
    store: UploadedPackagesVisualStore;
};

const FeatureFlagsModalContent = observer((props: Props & { canEdit: boolean }) => {
    const { visible, changeVisible, currentFeatureFlags, store, currentPackageId, canEdit } = props;
    const [form] = Form.useForm();
    const [preview, setPreview] = React.useState<PlanState>(null);
    const [refresh, setRefresh] = React.useState(0);
    const [saving, setSaving] = React.useState(false);
    const [openPanelKeys, setOpenPanelKeys] = React.useState<string[]>([]);
    const [featureFlagsOverride, setFeatureFlagsOverride] = React.useState<FeatureFlagsOverride>({
        overrideFeatureFlags: false,
        overrideImageFeatureFlags: false,
        overrideTextFeatureFlags: false
    });

    React.useEffect(() => {
        if (visible) {
            setFeatureFlags();
        } else {
            setFeatureFlagsOverride({
                overrideFeatureFlags: false,
                overrideImageFeatureFlags: false,
                overrideTextFeatureFlags: false
            });
            setOpenPanelKeys([]);
            form.resetFields();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible]);

    const handleSubmit = async () => {
        const featureFlags = transformFeatureFlagsAsRecord(form, featureFlagsOverride);
        if (
            !canEdit ||
            !preview?.value ||
            preview.key !== featureFlagsPreviewKeyOf(store.featureFlagsPlanning, [currentPackageId, featureFlags]) ||
            saving
        ) {
            return;
        }
        setSaving(true);
        try {
            const result = await store.updatePackageFeatureFlags(currentPackageId, featureFlags, preview.value);
            if (result) {
                changeVisible(false);
            } else {
                setRefresh(value => value + 1);
            }
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        changeVisible(false);
    };

    const hasModifiedFeatureFlags = React.useMemo(() => {
        if (currentFeatureFlags == null) {
            return false;
        }
        return Object.values(currentFeatureFlags).some(flag => flag != null && Object.keys(flag).length > 0);
    }, [currentFeatureFlags]);

    const setFeatureFlags = () => {
        if (!hasModifiedFeatureFlags) {
            setFormFeatureFlags(form, convertAlphaConfigsToFeatureFlags(store.defaultAlphaConfigs));
            return;
        }

        setFeatureFlagsOverride({
            ...setFormFeatureFlags(form, currentFeatureFlags)
        });
    };

    const featureFlagsPanelExtra = (onCheckboxChange: (e: CheckboxChangeEvent) => void, checked: boolean) => {
        return (
            <div
                onClick={e => {
                    e.stopPropagation();
                }}
            >
                <label>
                    Override flags:
                    <Checkbox
                        disabled={!canEdit}
                        style={{ marginLeft: 12 }}
                        checked={checked}
                        onChange={onCheckboxChange}
                    />
                </label>
            </div>
        );
    };

    return (
        <Modal
            title="Feature flags"
            className="alpha-portal-dialog two-columns"
            visible={visible}
            centered
            maskClosable={false}
            closable={false}
            width={600}
            footer={[
                <Button
                    key="feature-flags-dialog-close"
                    data-id="feature-flags-dialog-close"
                    className="light"
                    size="large"
                    onClick={handleCancel}
                >
                    Close
                </Button>,
                canEdit && (
                    <Button
                        key="feature-flags-dialog-save"
                        data-id="feature-flags-dialog-save"
                        type="primary"
                        size="large"
                        htmlType="submit"
                        form={formId}
                        disabled={!preview?.value || saving}
                        loading={saving}
                    >
                        {preview?.value
                            ? featureFlagProcessingLabels[preview.value.stage].save
                            : 'Checking processing choice'}
                    </Button>
                )
            ]}
            onCancel={handleCancel}
        >
            <>
                {preview?.value?.reason && <Alert type="warning" showIcon message={preview.value.reason} />}
                {preview?.error && (
                    <Alert
                        type="error"
                        showIcon
                        message={preview.error}
                        action={<Button onClick={() => setRefresh(value => value + 1)}>Refresh choice</Button>}
                    />
                )}
                {hasModifiedFeatureFlags && (
                    <div className="feature-flag-import-export-container">
                        <span
                            className="feature-flag-import-export-button export"
                            onClick={() => store.exportPackageFeautureFlags(currentPackageId)}
                        >
                            <i className="alpha-icon xs arrow-up" />
                            <span>Export feature flags</span>
                        </span>
                    </div>
                )}

                <Form
                    id={formId}
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    onValuesChange={() => setPreview(null)}
                >
                    {visible && canEdit && (
                        <Form.Item noStyle shouldUpdate>
                            {() => (
                                <FeatureFlagsPreview
                                    args={[
                                        currentPackageId,
                                        transformFeatureFlagsAsRecord(form, featureFlagsOverride, false)
                                    ]}
                                    planning={store.featureFlagsPlanning}
                                    refresh={refresh}
                                    onPreview={setPreview}
                                />
                            )}
                        </Form.Item>
                    )}
                    <Collapse activeKey={openPanelKeys} onChange={key => setOpenPanelKeys(key as string[])}>
                        <Collapse.Panel
                            key="1"
                            header="General feature flags"
                            forceRender
                            extra={featureFlagsPanelExtra(e => {
                                setFeatureFlagsOverride(prev => ({
                                    ...prev,
                                    overrideFeatureFlags: e.target.checked
                                }));
                            }, featureFlagsOverride.overrideFeatureFlags)}
                        >
                            <FeatureFlagsTreeRenderer
                                disableInputs={!canEdit || !featureFlagsOverride.overrideFeatureFlags}
                                projectId={store.currentProject?.id}
                            />
                        </Collapse.Panel>
                        <Collapse.Panel
                            key="2"
                            header="Text feature flags"
                            forceRender
                            extra={featureFlagsPanelExtra(e => {
                                setFeatureFlagsOverride(prev => ({
                                    ...prev,
                                    overrideTextFeatureFlags: e.target.checked
                                }));
                            }, featureFlagsOverride.overrideTextFeatureFlags)}
                        >
                            <FeatureFlagsTreeRenderer
                                disableInputs={!canEdit || !featureFlagsOverride.overrideTextFeatureFlags}
                                featureFlagKeyPrefix="TEXT::"
                                projectId={
                                    featureFlagsOverride.overrideTextFeatureFlags ? store.currentProject?.id : undefined
                                }
                            />
                        </Collapse.Panel>
                        <Collapse.Panel
                            key="3"
                            header="Images feature flags"
                            forceRender
                            extra={featureFlagsPanelExtra(e => {
                                setFeatureFlagsOverride(prev => ({
                                    ...prev,
                                    overrideImageFeatureFlags: e.target.checked
                                }));
                            }, featureFlagsOverride.overrideImageFeatureFlags)}
                        >
                            <FeatureFlagsTreeRenderer
                                disableInputs={!canEdit || !featureFlagsOverride.overrideImageFeatureFlags}
                                featureFlagKeyPrefix="IMAGE::"
                                projectId={
                                    featureFlagsOverride.overrideImageFeatureFlags
                                        ? store.currentProject?.id
                                        : undefined
                                }
                            />
                        </Collapse.Panel>
                    </Collapse>
                </Form>
            </>
        </Modal>
    );
});

const FeatureFlagsModal: React.FC<Props> = props => (
    <HasPermission
        entityId={props.store.currentProject?.id}
        permissionClaim={AppPermissions.CanEditProcessDocuments}
        yes={() => <FeatureFlagsModalContent {...props} canEdit />}
        no={() => <FeatureFlagsModalContent {...props} canEdit={false} />}
    />
);

export default observer(FeatureFlagsModal);
